import { Loader, useToastMessage } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import RegistryHeader from '../../../../_components/registry-header/RegistryHeader';
import { URL_REGISTRY } from '../../../../constants';
import { useSourceRegistryForm } from '../../../../hooks';
import { IPatternDefinition, ISourceRegistryRequestModel, ISourceRegistryResponseModel, ISourceRegistryValidationModel } from '../../../../models';
import { AppDispatch, useSelector } from '../../../../store/AppStore';
import getSourceDetails from '../../../../store/_api/sources/GetSourceName';
import useSourceNameValidation from '../../../../store/_api/sources/SourceNameValidation';
import useUpdateSourceRegistry from '../../../../store/_api/sources/UpdateSourceRegistry';
import { createSourceRegistryRequest } from '../../../../store/actions/sagaActions/sources/CreateSourceRegistrySagaActions';
import { createSourceRegistryFormSchema, getSourceRegistryResponseSchema, sourceRegistryNameSchema } from '../../../../utilities/sources/RegistriesSchemaDefintion';
import { ERRORS_SR } from '../../../../utilities/sources/RegistryUtils';
import { clearFormDataDynamicSource, isEqual, resetFormData, updateFormData, validateCreateFormData, validateFormData, validateName } from '../../../../utilities/sources/SourceRegistryUtil';
import SourceRegistryForm from '../source-registry-form/SourceRegistryForm';
import classes from './SourceRegistryWrapper.module.scss';

const replyTopic: { value: string, setValue: (arg: string) => void } = {
  value: '',
  setValue: (arg: string) => {
    replyTopic.value = arg;
  }
};

const SourceRegistryWrapper = () => {
  const { t: translate } = useTranslation();
  const HEADER_TITLE = {
    CREATEHEADERTITLE: translate('fsmHeaderTitle.CREATEHEADERTITLE'),
    VIEWHEADERTITLE: translate('fsmHeaderTitle.VIEWHEADERTITLE'),
    EDIT_HEADERTITLE: translate('fsmHeaderTitle.EDIT_HEADERTITLE'),
  };
  const REGISTRY_ERRORS = {
    duplicte_name: translate('registryErrors.duplicte_name'),
    duplicate_source_topic_name: translate('registryErrors.duplicate_source_topic_name'),
    duplicate_reply_topic_name: translate('registryErrors.duplicate_reply_topic_name')
  };
  const TOAST_MSG = {
    toast_success_msg_update: translate('sourceRegWrapperToast.toast_success_msg_update'),
    toast_fail_msg_update: translate('sourceRegWrapperToast.toast_fail_msg_update'),
    pls_try_again: translate('sourceRegWrapperToast.pls_try_again'),
    toast_success_msg: translate('sourceRegWrapperToast.toast_success_msg'),
    toast_identifier_invalid_msg: translate('sourceRegWrapperToast.toast_identifier_invalid_msg'),
    toast_fail_msg: translate('sourceRegWrapperToast.toast_fail_msg')
  };

  const toastType = {
    success: translate('toast_component.lbl_success'),
    error: translate('toast_component.lbl_error'),
    warning: translate('toast_component.lbl_warning')
  };

  const history = useNavigate();
  const location = useLocation();
  const { sourcename } = useParams<{ sourcename: string }>();
  const { version } = useParams<{ version: string }>();
  const { handleInputChange, formData, setFormParamValues, getFormParam } = useSourceRegistryForm();
  const { success: toastSuccess, failure, renderToast } = useToastMessage();
  const { fetchSourceNameDetails } = useSourceNameValidation();
  const { fetchSourceDetails } = getSourceDetails();
  const { updateSourceRegistry } = useUpdateSourceRegistry();
  const [isValid, setIsValid] = useState(true);
  const [identifiersArray, setIdentifiersArray] = useState<string[]>([]);
  const [patternList, setPatternList] = useState<IPatternDefinition[]>([]);
  const [isNameLoading, setIsNameLoading] = useState(false);
  const [isDuplicateIdn, setIsDuplicateIdn] = useState('');
  const [editStatusCode, setEditStatusCode] = useState<string | undefined>(undefined);
  const [view, setView] = useState(false);
  const [edit, setEdit] = useState(false);
  const [responseArray, setResponseArray] = useState<string[]>([]);
  const [updateSourceRegData, setUpdateSourceRegData] = useState<ISourceRegistryResponseModel>();
  const [headerTitle, setHeaderTitle] = useState<string>(sourcename ?? '');
  const [isCreateClicked, setIsCreateClicked] = useState(false);
  const [isShowPopUp, setIsShowPopUp] = useState(false);
  const [isSRNameLoading, setIsSRNameLoading] = useState(false);
  const [viewFormData, setViewFormData] = useState<any>();
  const [inputEventValue, setInputEventValue] = useState('');
  const [isCreateButtonDisabled, setIsCreateButtonDisabled] = useState(false);
  const [initialPatternData, setInitialPatternData] = useState<IPatternDefinition[]>([]);

  const identifierInvalidity = !formData.identifiers.isValid || !!isDuplicateIdn.length;

  const dispatch = useDispatch<AppDispatch>();
  const { sourceRegistryData, errorMessage, isLoading, success } = useSelector(
    (state) => state.sourceRegistryReducer
  );

  /**
   * @method nameValidationHandler
   * @description Handles the validation of the source registry name.
   * @param name - The name to be validated.
   */
  const nameValidationHandler = (name: string) => {
    if (formData.sourceRegistryName.value && formData.sourceRegistryName.isValid) {
      const resValidity = sourceRegistryNameSchema.validate(name);
      setIsSRNameLoading(true);
      if (resValidity.error) {
        failure(toastType.error, resValidity.error.details[0].message);
      } else {
        const response = fetchSourceNameDetails(name);
        response.then((data) => {
          setIsSRNameLoading(false);
          if (data.data && data.data?.sources?.length > 0) {
            validateName(data);
            setFormParamValues((prev) => {
              const newFormData = updateFormData(structuredClone(prev), REGISTRY_ERRORS.duplicte_name, false);
              return newFormData;
            });
          } else {
            setFormParamValues((prev) => {
              const newFormData = updateFormData(structuredClone(prev), '', true);
              return newFormData;
            });
          }
        }).catch(() => {
          setIsSRNameLoading(false);
        });
      }
    }
  };

  const responseDataIdentifierHandler = (identifier: any) => {
    if (identifier) {
      setIdentifiersArray(identifier.split(','));
      setResponseArray(identifier.split(','));
      formData.identifiersArray.value = identifier;
    }
  };

  /**
   * @method getSourceData
   * @description Fetches the source data for a given name and version.
   * @param name - The name of the source.
   * @param version - The version of the source.
   */
  const getSourceData = (name: string, version: string) => {
    setIsNameLoading(true);
    const data = fetchSourceDetails(name, version);
    data.then((responseData) => {
      const resValidity = getSourceRegistryResponseSchema.validate(responseData?.data);
      if (resValidity.error) {
        throw new Error(ERRORS_SR.noResponse);
      } else {
        responseDataIdentifierHandler(responseData?.data?.identifier);
        setFormParamValues((previousData: ISourceRegistryValidationModel) => {
          const newFormData = { ...previousData };
          newFormData.sourceRegistryName.value = responseData.data?.sourceName ?? '';
          newFormData.replyTimeOut.value = responseData.data?.replyTimeout ?? '';
          newFormData.replyToTopic.value = responseData.data?.replyTopicName ?? '';
          newFormData.requestSpecJson.value = responseData.data?.configJson?.requestSpecJson ?? '';
          newFormData.responseSpecJson.value = responseData.data?.configJson?.responseSpecJson ?? '';
          newFormData.eventName.value = responseData.data?.eventName ?? '';
          newFormData.eventName.version = responseData.data?.eventVersion ?? 0;
          newFormData.eventName.error = '';
          newFormData.eventName.isValid = true;
          setViewFormData(structuredClone(newFormData));
          return newFormData;
        });
        setInitialPatternData(responseData?.data?.patterns ?? []);
        setPatternList(responseData?.data?.patterns ?? []);
        setIsNameLoading(false);
      }
    }).catch(() => {
      setFormParamValues((previousData: ISourceRegistryValidationModel) => {
        const newFormData = updateFormData(previousData, '', true);
        return newFormData;
      });
      setIsNameLoading(false);
    });
  };

  /**
   * @method createButtonClicked
   * @description Handles the click event when the create button is clicked.
   */
  const createButtonClicked = () => {
    setIsCreateClicked(true);
    const [first] = formData.eventName.value.split(' ');
    const requestBody: ISourceRegistryRequestModel = {
      sourceName: formData.sourceRegistryName.value,
      identifier: identifiersArray.join(),
      eventName: first || null,
      eventVersion: formData.eventName.value && formData.eventName.version ? formData.eventName.version : null,
      configJson: formData.requestSpecJson.value ? { requestSpecJson: formData.requestSpecJson.value, responseSpecJson: '{\n "k": "k"\n}' } : null,
      replyTopicName: formData.replyToTopic.value ? formData.replyToTopic.value : null,
      replyTimeout: formData.replyTimeOut.value ? formData.replyTimeOut.value : null,
      patterns: patternList
    };
    const reqValidity = createSourceRegistryFormSchema.validate(requestBody);
    if (reqValidity.error) {
      failure(toastType.error, reqValidity.error.details[0].message);
    } else {
      setFormParamValues((prev) => {
        const [eventNameVal, , eventNameVer] = formData.eventName.value.split(' ');
        const newFormData = structuredClone(prev);
        newFormData.eventName.value = eventNameVal;
        newFormData.eventName.version = eventNameVer ? parseInt(eventNameVer.split(')')[0], 10) : 1;
        newFormData.eventName.isTouched = false;
        return newFormData;
      });
      dispatch(createSourceRegistryRequest(requestBody));
    }
  };

  /**
   * @method identifierDuplicateHandler
   * @description Handles the identifier duplicate event.
   * @param {string} isDuplicate - The duplicate identifier value.
   */
  const identifierDuplicateHandler = (isDuplicate: string) => {
    setIsDuplicateIdn(isDuplicate);
  };

  /**
   * @method editBtnClickHandler
   * @description Handles the click event of the edit button.
   * Sets the state variables to show the popup, enable editing mode, and disable viewing mode.
   * Navigates to the edit source registry page with the appropriate URL and state.
   */
  const editBtnClickHandler = () => {
    setIsShowPopUp(true);
    setIsCreateClicked(false);
    setEdit(true);
    setView(false);
    history({ pathname: `${URL_REGISTRY.EDIT_SOURCE_REGISTRY}/${sourcename}/${version}` }, { state: { path: location.state?.path, edit: true } });
  };

  /**
   * @method afterUpdateSource
   * @description Callback function called after updating the source registry.
   * @param data - The response data from the update operation.
   */
  const afterUpdateSource = (data: ISourceRegistryResponseModel) => {
    setIsNameLoading(false);
    setUpdateSourceRegData(data);
    toastSuccess(toastType.success, TOAST_MSG.toast_success_msg_update);
    history({ pathname: `${URL_REGISTRY.VIEW_SOURCE_REGISTRY}/${formData.sourceRegistryName.value}/${data.version}` }, { state: { path: location.state?.path } });
    setView(true);
    setEditStatusCode('');
  };

  /**
   * @method onUpdateHandler
   * @description Handles the update event for the SourceRegistryWrapper component.
   */
  const onUpdateHandler = () => {
    setIsCreateClicked(true);
    if (location.state) {
      location.state.edit = false;
    }
    const [first] = formData.eventName.value.split(' ');
    const requestBody: ISourceRegistryRequestModel = {
      sourceName: formData.sourceRegistryName.value,
      identifier: identifiersArray.join(),
      eventName: first || null,
      eventVersion: formData.eventName.version && formData.eventName.value ? formData.eventName.version : null,
      configJson: {
        requestSpecJson: formData.requestSpecJson.value ? formData.requestSpecJson.value : null,
        responseSpecJson: formData.responseSpecJson.value ? formData.responseSpecJson.value : null
      },
      replyTopicName: formData.replyToTopic.value ? formData.replyToTopic.value : null,
      replyTimeout: formData.replyTimeOut.value ? formData.replyTimeOut.value : null,
      patterns: patternList
    };
    const updateSourceResponse = updateSourceRegistry(requestBody, formData.sourceRegistryName.value, version ?? '1');
    setIsNameLoading(true);
    updateSourceResponse.then((data) => {
      if (data?.updateSourceRegistryData?.sourceName) {
        afterUpdateSource(data?.updateSourceRegistryData);
        setFormParamValues((prev) => {
          const [eventNameVal] = formData.eventName.value.split(' ');
          const newFormData = structuredClone(prev);
          newFormData.eventName.value = eventNameVal;
          newFormData.eventName.isTouched = false;
          return newFormData;
        });
      } else if (data.isError) {
        setIsNameLoading(false);
        setEditStatusCode(String(data.statusCode));
        failure(toastType.error, `${TOAST_MSG.toast_fail_msg_update} ${TOAST_MSG.pls_try_again}`);
      }
    });
  };

  /**
   * @method isCreateDisabled
   * @description Sets the disabled state of the create button.
   * @param isDisabled - A boolean value indicating whether the create button should be disabled.
   */
  const isCreateDisabled = (isDisabled: boolean) => {
    setIsCreateButtonDisabled(isDisabled);
  };

  const onDeleteSuccess = () => {
    getSourceData(`${sourcename}`, `${version}`);
  };

  useEffect(() => {
    if (sourcename && !location.state?.edit) { // for view mode
      setView(true);
      setEdit(false);
      getSourceData(sourcename, version ?? '1');
      setHeaderTitle(sourcename ?? '');
    } else if (sourcename && location.state?.edit) { // for edit mode
      setEdit(true);
      getSourceData(sourcename, version ?? '1');
      setHeaderTitle(sourcename ?? '');
    } else {
      setEdit(false);
      setView(false);
      setIsShowPopUp(true);
      setHeaderTitle(HEADER_TITLE.CREATEHEADERTITLE);
      setIdentifiersArray([]);
      setFormParamValues(resetFormData(formData));
      setPatternList([]);
    }
  }, [sourcename, location.state, translate]);

  useEffect(() => {
    if (sourcename && updateSourceRegData?.sourceName) { // when user land on view page after editing
      setView(true);
      setEdit(false);
      setHeaderTitle(sourcename ?? '');
    }
  }, [updateSourceRegData, sourcename]);

  useEffect(() => {
    const isIdnEqual = isEqual([...responseArray], [...identifiersArray]);
    if (edit) {
      const validityStatus = validateFormData({ formData, isDuplicate: isDuplicateIdn, identifiersArray, viewFormData, responseArray, initialPatternData, patternList });
      setIsValid(validityStatus);
    } else {
      const validityStatus = validateCreateFormData(formData, isDuplicateIdn, identifiersArray, isIdnEqual, patternList);
      setIsValid(validityStatus);
    }
  }, [formData, identifiersArray, isDuplicateIdn, responseArray, patternList, inputEventValue, initialPatternData]);

  useEffect(() => {
    if (success && sourceRegistryData && sourceRegistryData.length > 0) {
      toastSuccess(toastType.success, TOAST_MSG.toast_success_msg);
      history(`${URL_REGISTRY.VIEW_SOURCE_REGISTRY}/${sourceRegistryData[0]?.sourceName}/${sourceRegistryData[0]?.version}`);
      setView(true);
    } else if (errorMessage) {
      failure(toastType.error, errorMessage);
    }
  }, [sourceRegistryData, errorMessage]);

  // when same state of identifier is added to other source registry
  useEffect(() => {
    if (editStatusCode === ERRORS_SR.statusCode) {
      failure(toastType.error, TOAST_MSG.toast_identifier_invalid_msg);
    }
  }, [editStatusCode]);

  useEffect(() => {
    const unloadCallback = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    if (!view) {
      window.addEventListener('beforeunload', unloadCallback);
    }
    return () => window.removeEventListener('beforeunload', unloadCallback);
  }, [view]);

  useEffect(() => () => {
    clearFormDataDynamicSource(formData, [formData.sourceRegistryName.name, formData.identifiers.name, formData.eventName.name, formData.requestSpecJson.name, formData.replyTimeOut.name, formData.replyToTopic.name]);
  }, []);

  return (
    <Row>
      <Col md={{ offset: 1, span: 10 }}>
        <RegistryHeader
          headerTitle={headerTitle}
          onCreateHandler={createButtonClicked}
          path={URL_REGISTRY.SOURCE_REGISTRY}
          disableCreate={isValid || isCreateButtonDisabled}
          view={view}
          edit={edit}
          editBtnClickHandler={editBtnClickHandler}
          onUpdateHandler={onUpdateHandler}
          confirmDialogMsg={edit ? translate(
            'source_registry_wrapper.confirm_dialog_edit_msg'
          ) : translate(
            'source_registry_wrapper.confirm_dialog_msg'
          )}
          isShowPopUp={isShowPopUp}
          isCreateClicked={isCreateClicked}
          onDeleteSuccess={onDeleteSuccess}
        />
        <div className={classes['cp-sr-wrapper']} data-testid="parent">
          <Loader show={isLoading || isNameLoading} />
          {renderToast()}
          <div className={classes['cp-sr-form-wrapper']}>
            <SourceRegistryForm
              formData={formData}
              handleInputChange={handleInputChange}
              validateSourceRegistryName={nameValidationHandler}
              identifierDuplicate={identifierDuplicateHandler}
              setIdentifiersArray={setIdentifiersArray}
              view={view}
              resIdentifierArray={identifiersArray}
              identifierInvalidity={identifierInvalidity}
              setFormParamValues={setFormParamValues}
              getFormParam={getFormParam}
              edit={edit}
              isSRNameLoading={isSRNameLoading}
              setPatternList={setPatternList}
              patternList={patternList}
              isCreateButtonDisabled={isCreateButtonDisabled}
              setDisableCreateButton={isCreateDisabled}
              inputEventValue={inputEventValue}
              setInputEventValue={setInputEventValue}
            />
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default SourceRegistryWrapper;
